import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { 
  ArrowLeft, Plus, X, Search, Scale, ChevronDown, ChevronUp,
  Download, Lightbulb, CheckCircle, Info, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MaterialCompare() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [materials, setMaterials] = useState([]);
  const [compareList, setCompareList] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [expandedSpecs, setExpandedSpecs] = useState(true);
  const [expandedTech, setExpandedTech] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchMaterials();
    const ids = searchParams.get('ids');
    if (ids) {
      const materialIds = ids.split(',').filter(Boolean);
      loadMaterialsFromIds(materialIds);
    }
  }, []);

  // Fetch comparison data when compareList changes
  useEffect(() => {
    if (compareList.length >= 2) {
      fetchComparisonData();
    } else {
      setComparisonData(null);
    }
  }, [compareList]);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials?limit=500`, { withCredentials: true });
      const materialData = Array.isArray(response.data) ? response.data : response.data.materials || [];
      setMaterials(materialData);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMaterialsFromIds = async (ids) => {
    try {
      const promises = ids.slice(0, 4).map(id => 
        axios.get(`${API}/materials/${id}`, { withCredentials: true })
      );
      const responses = await Promise.all(promises);
      setCompareList(responses.map(r => r.data).filter(Boolean));
    } catch (error) {
      console.error('Failed to load materials from URL:', error);
    }
  };

  const fetchComparisonData = async () => {
    if (compareList.length < 2) return;
    
    try {
      const response = await axios.post(`${API}/similar/compare`, 
        compareList.map(m => m.material_id),
        { withCredentials: true }
      );
      setComparisonData(response.data);
    } catch (error) {
      console.error('Failed to fetch comparison data:', error);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (term.length > 1) {
      const results = materials.filter(m => 
        m.name.toLowerCase().includes(term.toLowerCase()) ||
        m.stone_type?.toLowerCase().includes(term.toLowerCase()) ||
        m.color?.toLowerCase().includes(term.toLowerCase())
      ).slice(0, 10);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const addToCompare = (material) => {
    if (compareList.length >= 4) {
      toast.error('Maximum 4 materials');
      return;
    }
    if (compareList.find(m => m.material_id === material.material_id)) {
      toast.error('Already in comparison');
      return;
    }
    const newList = [...compareList, material];
    setCompareList(newList);
    setShowSearch(false);
    setSearchTerm('');
    setSearchResults([]);
    
    navigate(`/compare?ids=${newList.map(m => m.material_id).join(',')}`, { replace: true });
  };

  const removeFromCompare = (materialId) => {
    const newList = compareList.filter(m => m.material_id !== materialId);
    setCompareList(newList);
    if (newList.length > 0) {
      navigate(`/compare?ids=${newList.map(m => m.material_id).join(',')}`, { replace: true });
    } else {
      navigate('/compare', { replace: true });
    }
  };

  const clearAll = () => {
    setCompareList([]);
    setComparisonData(null);
    navigate('/compare', { replace: true });
  };

  const downloadPdf = async () => {
    if (compareList.length < 2) return;
    
    setDownloadingPdf(true);
    try {
      const response = await axios.post(`${API}/similar/compare/pdf`,
        compareList.map(m => m.material_id),
        { withCredentials: true }
      );
      
      // Download PDF
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.pdf_base64}`;
      link.download = response.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="material-compare-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-neutral-500 mb-6">
          <button onClick={() => navigate(-1)} className="hover:text-neutral-900 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <span>/</span>
          <span className="text-neutral-900">Compare Materials</span>
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Scale className="w-7 h-7" />
                <h1 className="text-3xl font-light">Compare Materials</h1>
              </div>
              <p className="text-neutral-500">
                Compare up to 4 materials side by side
              </p>
            </div>
            
            {compareList.length >= 2 && (
              <div className="flex items-center gap-3">
                <button
                  onClick={downloadPdf}
                  disabled={downloadingPdf}
                  className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 hover:bg-neutral-800 disabled:opacity-50"
                  data-testid="download-pdf-btn"
                >
                  <Download className="w-4 h-4" />
                  {downloadingPdf ? 'Generating...' : 'Export PDF'}
                </button>
                <button
                  onClick={clearAll}
                  className="text-neutral-500 hover:text-neutral-900"
                >
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Comparison Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[0, 1, 2, 3].map((index) => {
            const material = compareList[index];
            return (
              <div key={index} className="relative">
                {material ? (
                  <div className="bg-white border border-neutral-200 overflow-hidden">
                    <button
                      onClick={() => removeFromCompare(material.material_id)}
                      className="absolute top-2 right-2 z-10 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center hover:bg-red-50"
                      data-testid={`remove-material-${material.material_id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                    
                    <div className="aspect-square bg-neutral-100 overflow-hidden">
                      {material.images?.[0] ? (
                        <img 
                          src={material.images[0]} 
                          alt={material.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Layers className="w-12 h-12 text-neutral-300" />
                        </div>
                      )}
                    </div>
                    
                    <div className="p-4">
                      <h3 className="font-medium truncate">{material.name}</h3>
                      <p className="text-sm text-neutral-500">{material.stone_type}</p>
                      <Link 
                        to={`/materials/${material.material_id}`}
                        className="text-xs text-neutral-600 hover:text-neutral-900 mt-2 block"
                      >
                        View Details →
                      </Link>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowSearch(true)}
                    className="w-full aspect-[3/4] border-2 border-dashed border-neutral-300 hover:border-neutral-500 hover:bg-neutral-50 transition-colors flex flex-col items-center justify-center gap-3"
                    data-testid="add-material-slot"
                  >
                    <Plus className="w-8 h-8 text-neutral-400" />
                    <span className="text-sm text-neutral-500">Add Material</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Search Modal */}
        {showSearch && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20">
            <div className="bg-white w-full max-w-xl mx-4 shadow-2xl">
              <div className="p-4 border-b flex items-center gap-3">
                <Search className="w-5 h-5 text-neutral-400" />
                <input
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search materials..."
                  className="flex-1 text-lg outline-none"
                  autoFocus
                  data-testid="search-material-input"
                />
                <button onClick={() => { setShowSearch(false); setSearchTerm(''); setSearchResults([]); }}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="max-h-96 overflow-y-auto">
                {searchResults.length > 0 ? (
                  searchResults.map(material => (
                    <button
                      key={material.material_id}
                      onClick={() => addToCompare(material)}
                      className="w-full p-4 flex items-center gap-4 hover:bg-neutral-50 border-b last:border-0"
                      data-testid={`search-result-${material.material_id}`}
                    >
                      <div className="w-14 h-14 bg-neutral-100 overflow-hidden flex-shrink-0">
                        {material.images?.[0] && (
                          <img src={material.images[0]} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{material.name}</p>
                        <p className="text-sm text-neutral-500">{material.stone_type} • {material.color}</p>
                      </div>
                    </button>
                  ))
                ) : searchTerm.length > 1 ? (
                  <div className="p-8 text-center text-neutral-500">No materials found</div>
                ) : (
                  <div className="p-8 text-center text-neutral-500">Start typing to search...</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Comparison Table */}
        {comparisonData && (
          <>
            {/* Basic Specs */}
            <div className="bg-white border border-neutral-200 mb-6">
              <button
                onClick={() => setExpandedSpecs(!expandedSpecs)}
                className="w-full p-4 flex items-center justify-between border-b hover:bg-neutral-50"
              >
                <h2 className="text-lg font-medium">Specifications Comparison</h2>
                {expandedSpecs ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              {expandedSpecs && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-neutral-50">
                        <th className="p-4 text-left text-sm font-medium text-neutral-500 w-40">Attribute</th>
                        {comparisonData.comparison_table.material_names.map((name, i) => (
                          <th key={i} className="p-4 text-left text-sm font-medium">{name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.comparison_table.rows.map((row, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="p-4 text-sm font-medium text-neutral-500">{row.attribute}</td>
                          {row.values.map((val, j) => (
                            <td key={j} className="p-4 text-sm">{val}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Recommendations */}
            {comparisonData.recommendations && comparisonData.recommendations.length > 0 && (
              <div className="bg-white border border-neutral-200 mb-6">
                <div className="p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-500" />
                    <h2 className="text-lg font-medium">Recommendations</h2>
                  </div>
                </div>
                <div className="p-4 grid md:grid-cols-3 gap-4">
                  {comparisonData.recommendations.map((rec, i) => (
                    <div key={i} className="bg-neutral-50 p-4 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-sm">{rec.category}</span>
                      </div>
                      <p className="text-lg font-medium mb-1">{rec.material_name}</p>
                      <p className="text-sm text-neutral-500">{rec.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Common Applications */}
            {comparisonData.common_applications && comparisonData.common_applications.length > 0 && (
              <div className="bg-neutral-100 p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Info className="w-5 h-5 text-neutral-500" />
                  <span className="font-medium">All materials are suitable for:</span>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {comparisonData.common_applications.map((app, i) => (
                    <span key={i} className="bg-white px-3 py-1 text-sm">
                      {app.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {compareList.length === 0 && (
          <div className="text-center py-16">
            <Scale className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
            <h3 className="text-xl font-medium mb-2">No materials to compare</h3>
            <p className="text-neutral-500 mb-6">
              Add materials from the library to compare specifications
            </p>
            <button
              onClick={() => navigate('/materials')}
              className="bg-neutral-900 text-white px-6 py-3 hover:bg-neutral-800"
            >
              Browse Materials
            </button>
          </div>
        )}

        {/* Need more materials message */}
        {compareList.length === 1 && (
          <div className="bg-yellow-50 border border-yellow-200 p-6 text-center">
            <p className="text-yellow-800">Add at least one more material to see the comparison table</p>
          </div>
        )}
      </main>
    </div>
  );
}
